/**
 * Iter-118.13 PART 3 — Lightweight global search.
 *
 * Triggered from the search icon in `ProjectHeader`. Modal overlay with
 * a single text input that hits `GET /projects/{pid}/search?q=...` and
 * groups results by type: Scenes / Cast / Notes / Images / Documents /
 * Schedules / Flags. Tapping a row navigates straight to the relevant
 * surface and closes the dialog.
 *
 * No fancy ranking, no advanced filters — fast triage for "find the
 * thing in my project right now". 250 ms debounce keeps it responsive
 * without hammering the backend on every keystroke.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Search,
    X,
    Clapperboard,
    UserRound,
    StickyNote,
    Image as ImageIcon,
    FileText,
    CalendarDays,
    AlertTriangle,
    Loader2,
} from "lucide-react";
import { api } from "../lib/api";
import SearchErrorBoundary from "./SearchErrorBoundary";
import { mediaUrl } from "../lib/media";
import { searchEndpoint } from "../lib/searchEndpoint";
import { useApiBase, useRouteBase } from "../context/ApiBaseContext";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";

const GROUPS = [
    { key: "scenes", label: "Scenes", icon: Clapperboard },
    { key: "cast", label: "Cast", icon: UserRound },
    { key: "notes", label: "Notes", icon: StickyNote },
    { key: "images", label: "Images", icon: ImageIcon },
    { key: "documents", label: "Documents", icon: FileText },
    { key: "schedules", label: "Schedules", icon: CalendarDays },
    { key: "flags", label: "Flags", icon: AlertTriangle },
];

function combinedSceneNumber(s) {
    if (!s) return "—";
    if (s.episode && s.scene_number) return `${s.episode}.${s.scene_number}`;
    return s.scene_number || "—";
}

export default function GlobalSearchDialog({ open, onOpenChange, projectId }) {
    const navigate = useNavigate();
    const apiBase = useApiBase();
    const routeBase = useRouteBase();
    const [q, setQ] = useState("");
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const inputRef = useRef(null);
    const debounceRef = useRef(null);

    // Reset on close so a stale query doesn't flash next time.
    useEffect(() => {
        if (!open) {
            setQ("");
            setResults(null);
            setLoading(false);
            return;
        }
        // Tiny delay so the dialog mounts before we steal focus.
        const t = setTimeout(() => inputRef.current?.focus(), 60);
        return () => clearTimeout(t);
    }, [open]);

    // Debounced fetch.
    useEffect(() => {
        if (!open) return;
        const trimmed = q.trim();
        if (!trimmed) {
            setResults(null);
            setLoading(false);
            return;
        }
        if (debounceRef.current) clearTimeout(debounceRef.current);
        setLoading(true);
        debounceRef.current = setTimeout(async () => {
            try {
                const { data } = await api.get(
                    searchEndpoint(apiBase, projectId),
                    { params: { q: trimmed } },
                );
                setResults(data);
            } catch {
                setResults(null);
            } finally {
                setLoading(false);
            }
        }, 250);
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [q, open, projectId, apiBase]);

    const totalHits = useMemo(() => {
        if (!results) return 0;
        return GROUPS.reduce(
            (acc, g) => acc + (Array.isArray(results[g.key]) ? results[g.key].length : 0),
            0,
        );
    }, [results]);

    const close = () => onOpenChange?.(false);

    const go = (path) => {
        // Iter-121 — defensive navigation. Any unexpected throw
        // (bad path string, navigate hook mis-wire, stale router
        // context) must NOT crash the app. We log and bail; the
        // dialog still closes so the user can try another row.
        close();
        setTimeout(() => {
            try {
                if (typeof path !== "string" || !path.startsWith("/")) {
                    // eslint-disable-next-line no-console
                    console.warn("GlobalSearch: refusing to navigate to", path);
                    return;
                }
                navigate(path);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error("GlobalSearch navigation failed:", err);
            }
        }, 30);
    };

    const renderRow = (group, item) => {
        const base = "tap w-full flex items-center gap-3 rounded-lg border border-white/10 bg-card p-2.5 hover:bg-white/5 text-left";
        switch (group) {
            case "scenes": {
                const num = combinedSceneNumber(item);
                const isOmitted = !!item.omitted;
                return (
                    <button
                        key={`scene-${item.id}`}
                        type="button"
                        data-testid={`global-search-scene-${item.id}`}
                        onClick={() => go(`${routeBase}/${projectId}/scenes/${item.id}`)}
                        className={`tap w-full flex items-center gap-3 rounded-lg border p-2.5 text-left ${
                            isOmitted
                                ? "border-dashed border-amber-400/60 bg-amber-500/10 hover:bg-amber-500/15"
                                : "border-white/10 bg-card hover:bg-white/5"
                        }`}
                    >
                        <span
                            className={`mono text-xs font-bold shrink-0 min-w-[44px] text-right ${
                                isOmitted ? "text-amber-100 line-through" : "text-primary"
                            }`}
                        >
                            {num}
                        </span>
                        <span className="flex-1 min-w-0">
                            <span className={`block text-sm truncate ${isOmitted ? "text-amber-50/95" : "text-white"}`}>
                                {item.heading || "(No heading)"}
                            </span>
                            <span className="mt-0.5 flex items-center gap-1.5 flex-wrap">
                                {isOmitted ? (
                                    <span
                                        data-testid={`global-search-scene-${item.id}-omitted-badge`}
                                        className="inline-flex items-center px-1.5 h-4 rounded-full text-[9px] font-semibold uppercase tracking-[0.15em] bg-amber-500/30 text-amber-50 border border-amber-400/60"
                                    >
                                        {item.omitted_by_revision_id ? "Omitted by revision" : "Omitted in script"}
                                    </span>
                                ) : null}
                                {item.added_by_revision_id && !isOmitted ? (
                                    <span
                                        data-testid={`global-search-scene-${item.id}-inserted-badge`}
                                        className="inline-flex items-center px-1.5 h-4 rounded-full text-[9px] font-semibold uppercase tracking-[0.15em] bg-emerald-500/25 text-emerald-100 border border-emerald-400/50"
                                    >
                                        Inserted
                                    </span>
                                ) : null}
                                <span className={`text-[11px] truncate ${isOmitted ? "text-amber-100/80" : "text-neutral-500"}`}>
                                    {[item.int_ext, item.time_of_day].filter(Boolean).join(" · ") || "—"}
                                </span>
                            </span>
                            {/* Iter-120 — surface scene-level Look chip in
                                the search row so users find "Rain" /
                                "Bloody" scenes at a glance. */}
                            {item.look ? (
                                <span
                                    data-testid={`global-search-scene-${item.id}-look`}
                                    className="inline-flex items-center gap-1 mt-0.5 px-1.5 h-4 rounded-full text-[9px] font-semibold uppercase tracking-[0.15em] bg-primary/15 text-primary border border-primary/30"
                                >
                                    Look · {item.look}
                                </span>
                            ) : null}
                        </span>
                    </button>
                );
            }
            case "cast":
                return (
                    <button
                        key={`cast-${item.id}`}
                        type="button"
                        data-testid={`global-search-cast-${item.id}`}
                        onClick={() => go(`${routeBase}/${projectId}/characters/${item.id}`)}
                        className={base}
                    >
                        {item.character_number ? (
                            <span className="mono text-[10px] text-neutral-400 shrink-0 min-w-[44px] text-right">
                                #{item.character_number}
                            </span>
                        ) : (
                            <span className="shrink-0 min-w-[44px]" />
                        )}
                        <span className="flex-1 min-w-0">
                            <span className="block text-sm text-white truncate">
                                {item.name}
                            </span>
                            <span className="flex items-center gap-1.5 flex-wrap mt-0.5">
                                {item.actor ? (
                                    <span className="text-[11px] text-neutral-500 truncate">
                                        {item.actor}
                                    </span>
                                ) : null}
                                {/* Iter-118.14 — episode pills so a character
                                    that appears in multiple episodes is
                                    instantly disambiguated. */}
                                {Array.isArray(item.episodes) && item.episodes.length > 0 ? (
                                    <span className="flex items-center gap-1 flex-wrap">
                                        {item.episodes.slice(0, 4).map((ep) => (
                                            <span
                                                key={ep}
                                                data-testid={`global-search-cast-${item.id}-ep-${ep}`}
                                                className="inline-flex items-center px-1.5 h-4 rounded-full text-[9px] font-semibold uppercase tracking-[0.15em] bg-primary/15 text-primary border border-primary/30"
                                            >
                                                Ep {ep}
                                            </span>
                                        ))}
                                        {item.episodes.length > 4 ? (
                                            <span className="text-[10px] text-neutral-500">
                                                +{item.episodes.length - 4}
                                            </span>
                                        ) : null}
                                    </span>
                                ) : null}
                            </span>
                        </span>
                    </button>
                );
            case "notes": {
                // Iter-121 — supervisor note search results must NEVER
                // navigate to a non-existent route. Previously we
                // fell back to `/projects/{pid}/notes` which has no
                // matching <Route> and triggered the catch-all
                // redirect to `/`, making the app appear to crash
                // back to the project selection screen.
                const target = item.scene_id
                    ? `${routeBase}/${projectId}/scenes/${item.scene_id}`
                    : item.character_id
                        ? `${routeBase}/${projectId}/characters/${item.character_id}`
                        : `${routeBase}/${projectId}/supervisor-notes`;
                return (
                    <button
                        key={`note-${item.id}`}
                        type="button"
                        data-testid={`global-search-note-${item.id}`}
                        onClick={() => go(target)}
                        className={base}
                    >
                        <StickyNote size={14} className="text-neutral-500 shrink-0" />
                        <span className="flex-1 min-w-0">
                            <span className="block text-sm text-white line-clamp-2">
                                {item.text || "—"}
                            </span>
                            <span className="block text-[11px] text-neutral-500 truncate">
                                {[
                                    item.scene_number ? `Sc ${item.scene_number}` : null,
                                    item.character_name,
                                    item.label,
                                ]
                                    .filter(Boolean)
                                    .join(" · ") || "Note"}
                            </span>
                        </span>
                    </button>
                );
            }
            case "images": {
                // Iter-118.14 — episode-prefixed scene label (e.g. "1.39")
                // so episodic projects disambiguate at a glance.
                const sceneLabel = item.scene_number
                    ? item.scene_episode
                        ? `${item.scene_episode}.${item.scene_number}`
                        : `Sc ${item.scene_number}`
                    : null;
                return (
                    <button
                        key={`img-${item.id}`}
                        type="button"
                        data-testid={`global-search-image-${item.id}`}
                        onClick={() => go(`${routeBase}/${projectId}/state-entries/${item.id}`)}
                        className={base}
                    >
                        {item.media_id ? (
                            <img
                                src={mediaUrl(projectId, item.media_id, "thumb", { apiBase })}
                                alt=""
                                className="w-10 h-10 rounded object-cover bg-secondary shrink-0"
                                loading="lazy"
                            />
                        ) : (
                            <span className="w-10 h-10 rounded bg-secondary shrink-0 flex items-center justify-center">
                                <ImageIcon size={14} className="text-neutral-500" />
                            </span>
                        )}
                        <span className="flex-1 min-w-0">
                            <span className="block text-sm text-white truncate">
                                {item.look || item.note || "Image"}
                            </span>
                            <span className="block text-[11px] text-neutral-500 truncate">
                                {[sceneLabel, item.character_name]
                                    .filter(Boolean)
                                    .join(" · ") || "—"}
                            </span>
                            {/* Iter-120 — show the parent scene's Look on
                                image rows when the entry itself doesn't
                                already advertise a more specific look. */}
                            {item.scene_look && !item.look ? (
                                <span
                                    data-testid={`global-search-image-${item.id}-scene-look`}
                                    className="inline-flex items-center gap-1 mt-0.5 px-1.5 h-4 rounded-full text-[9px] font-semibold uppercase tracking-[0.15em] bg-primary/10 text-primary/90 border border-primary/20"
                                >
                                    Scene · {item.scene_look}
                                </span>
                            ) : null}
                        </span>
                    </button>
                );
            }
            case "documents":
                // Iter-121 — map change_documents to a real route.
                // Prefer scene → character → script page (where
                // uploaded script revisions live). Previously we
                // hard-coded `/notes` which did not exist and the
                // router fell through to the catch-all, bouncing
                // users to the project-list screen mid-search.
                return (
                    <button
                        key={`doc-${item.id}`}
                        type="button"
                        data-testid={`global-search-doc-${item.id}`}
                        onClick={() => go(
                            item.scene_id
                                ? `${routeBase}/${projectId}/scenes/${item.scene_id}`
                                : item.character_id
                                    ? `${routeBase}/${projectId}/characters/${item.character_id}`
                                    : `${routeBase}/${projectId}/script`,
                        )}
                        className={base}
                    >
                        <FileText size={14} className="text-neutral-500 shrink-0" />
                        <span className="flex-1 min-w-0">
                            <span className="block text-sm text-white truncate">
                                {item.label || item.filename || "Document"}
                            </span>
                            <span className="block text-[11px] text-neutral-500 truncate">
                                {[
                                    item.kind,
                                    item.scene_number ? `Sc ${item.scene_number}` : null,
                                    item.character_name,
                                ]
                                    .filter(Boolean)
                                    .join(" · ") || item.filename}
                            </span>
                        </span>
                    </button>
                );
            case "schedules":
                return (
                    <button
                        key={`sched-${item.id}`}
                        type="button"
                        data-testid={`global-search-schedule-${item.id}`}
                        onClick={() => go(`${routeBase}/${projectId}/schedule`)}
                        className={base}
                    >
                        <CalendarDays size={14} className="text-neutral-500 shrink-0" />
                        <span className="flex-1 min-w-0">
                            <span className="block text-sm text-white truncate">
                                {item.title || item.filename || "Schedule"}
                            </span>
                            <span className="block text-[11px] text-neutral-500 truncate">
                                {[item.shoot_date, item.shoot_day]
                                    .filter(Boolean)
                                    .join(" · ") || "—"}
                            </span>
                        </span>
                    </button>
                );
            case "flags":
                return (
                    <button
                        key={`flag-${item.id}`}
                        type="button"
                        data-testid={`global-search-flag-${item.id}`}
                        onClick={() => go(`${routeBase}/${projectId}/flags?flag=${item.id}`)}
                        className={base}
                    >
                        <AlertTriangle size={14} className="text-amber-300 shrink-0" />
                        <span className="flex-1 min-w-0">
                            <span className="block text-sm text-white line-clamp-2">
                                {item.message || `Flag · ${item.entity_type}`}
                            </span>
                            <span className="block text-[11px] text-neutral-500 truncate">
                                {[item.entity_label, item.created_by_name, item.status]
                                    .filter(Boolean)
                                    .join(" · ")}
                            </span>
                        </span>
                    </button>
                );
            default:
                return null;
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="global-search-dialog"
                className="max-w-lg p-0 gap-0 overflow-hidden"
            >
                <DialogHeader className="p-4 pb-3 border-b border-white/5">
                    <DialogTitle className="text-sm">Search this project</DialogTitle>
                    <DialogDescription className="sr-only">
                        Search across scenes, cast, notes, images, documents,
                        schedules and flags in this project.
                    </DialogDescription>
                </DialogHeader>
                <div className="p-4 pb-3">
                    <div className="relative">
                        <Search
                            size={16}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none"
                        />
                        <input
                            ref={inputRef}
                            data-testid="global-search-input"
                            type="text"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search scenes, cast, notes, looks…"
                            className="w-full h-11 pl-10 pr-9 rounded-md bg-secondary text-white placeholder:text-neutral-500 border border-transparent focus:outline-none focus:ring-1 focus:ring-primary"
                            autoComplete="off"
                            spellCheck={false}
                        />
                        {q ? (
                            <button
                                type="button"
                                onClick={() => setQ("")}
                                data-testid="global-search-clear"
                                aria-label="Clear search"
                                className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-7 w-7 rounded text-neutral-400 hover:text-white hover:bg-white/5"
                            >
                                <X size={14} />
                            </button>
                        ) : null}
                    </div>
                </div>
                <div
                    data-testid="global-search-results"
                    className="max-h-[60vh] overflow-y-auto px-4 pb-4 space-y-4"
                >
                    {!q.trim() ? (
                        <p
                            data-testid="global-search-hint"
                            className="text-xs text-neutral-500 leading-relaxed py-6 text-center"
                        >
                            Search any word or number across scenes, cast, notes,
                            looks, documents, schedules and flags. Try things like
                            <code className="mx-1 px-1 py-0.5 rounded bg-secondary">
                                1.4
                            </code>
                            for episode 1 scene 4, a character name, or a look.
                        </p>
                    ) : loading ? (
                        <div
                            data-testid="global-search-loading"
                            className="flex items-center gap-2 justify-center py-8 text-xs text-neutral-500"
                        >
                            <Loader2 size={14} className="animate-spin" />
                            Searching…
                        </div>
                    ) : !results || totalHits === 0 ? (
                        <p
                            data-testid="global-search-empty"
                            className="text-xs text-neutral-500 leading-relaxed py-6 text-center"
                        >
                            Nothing matches “{q.trim()}”. Try a different keyword,
                            scene number, or look.
                        </p>
                    ) : (
                        <SearchErrorBoundary onReset={() => setQ(q + " ")}>
                            {GROUPS.map((g) => {
                                const items = results[g.key] || [];
                                if (items.length === 0) return null;
                                const Icon = g.icon;
                                return (
                                    <section
                                        key={g.key}
                                        data-testid={`global-search-group-${g.key}`}
                                    >
                                        <h3 className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-2">
                                            <Icon size={11} />
                                            {g.label}
                                            <span className="text-neutral-600">·</span>
                                            <span className="text-neutral-400">{items.length}</span>
                                        </h3>
                                        <div className="space-y-1.5">
                                            {items.map((it) => renderRow(g.key, it))}
                                        </div>
                                    </section>
                                );
                            })}
                        </SearchErrorBoundary>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
